import React, { useState } from 'react';
import { motion } from 'framer-motion';

interface DNSRecord {
  type: string;
  name: string;
  value: string;
  priority?: number;
  purpose: string;
}

export const HappyHumanToolsSESSetup: React.FC = () => {
  const [domain] = useState('happyhumantools.com');
  const [verificationCode] = useState(() => {
    // Generate a mock verification code for display
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let code = '';
    for (let i = 0; i < 32; i++) {
      code += chars[Math.floor(Math.random() * chars.length)];
    }
    return code;
  });

  const [dkimTokens] = useState(() => [
    'k7kdq7asnc2bqny4v6xbqfnzqpvqzjx3',
    'bx5hzs3wm5vkfqxhtsz5n6xqnkyh7qjp',
    'q3k7jnr4b5xnwqzfvhq2nxkpz5vqjxh7'
  ]);

  const dnsRecords: DNSRecord[] = [
    // Domain Verification
    {
      type: 'TXT',
      name: '_amazonses.happyhumantools.com',
      value: `amazonses:${verificationCode}`,
      purpose: 'Domain ownership verification'
    },
    // DKIM Records
    {
      type: 'CNAME',
      name: `${dkimTokens[0]}._domainkey.happyhumantools.com`,
      value: `${dkimTokens[0]}.dkim.amazonses.com`,
      purpose: 'DKIM signature 1 for email authenticity'
    },
    {
      type: 'CNAME',
      name: `${dkimTokens[1]}._domainkey.happyhumantools.com`,
      value: `${dkimTokens[1]}.dkim.amazonses.com`,
      purpose: 'DKIM signature 2 for email authenticity'
    },
    {
      type: 'CNAME',
      name: `${dkimTokens[2]}._domainkey.happyhumantools.com`,
      value: `${dkimTokens[2]}.dkim.amazonses.com`,
      purpose: 'DKIM signature 3 for email authenticity'
    },
    // SPF Record
    {
      type: 'TXT',
      name: 'happyhumantools.com',
      value: 'v=spf1 include:amazonses.com ~all',
      purpose: 'SPF - Authorize AWS SES to send emails'
    },
    // DMARC Record
    {
      type: 'TXT',
      name: '_dmarc.happyhumantools.com',
      value: 'v=DMARC1; p=quarantine; rua=mailto:admin@happyhumantools.com; pct=100',
      purpose: 'DMARC - Email authentication policy'
    },
    // MX Record for receiving emails
    {
      type: 'MX',
      name: 'happyhumantools.com',
      value: 'inbound-smtp.us-west-2.amazonaws.com',
      priority: 10,
      purpose: 'MX - Route incoming emails to AWS SES'
    },
    // Custom MAIL FROM domain
    {
      type: 'MX',
      name: 'mail.happyhumantools.com',
      value: 'feedback-smtp.us-west-2.amazonses.com',
      priority: 10,
      purpose: 'Custom MAIL FROM domain'
    },
    {
      type: 'TXT',
      name: 'mail.happyhumantools.com',
      value: 'v=spf1 include:amazonses.com ~all',
      purpose: 'SPF for MAIL FROM domain'
    }
  ];

  const [copiedRecord, setCopiedRecord] = useState<string | null>(null);

  const copyToClipboard = (text: string, recordName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedRecord(recordName);
    setTimeout(() => setCopiedRecord(null), 2000);
  };

  const awsSESSteps = [
    {
      step: 1,
      title: 'Access AWS SES Console',
      description: 'Log into AWS Console and navigate to SES in us-west-2 region',
      link: 'https://us-west-2.console.aws.amazon.com/ses/home?region=us-west-2'
    },
    {
      step: 2,
      title: 'Add Domain',
      description: 'Click "Verified identities" → "Create identity" → Choose "Domain"',
      input: domain
    },
    {
      step: 3,
      title: 'Add DNS Records',
      description: 'Add these records to your domain DNS provider (CloudFlare, Route53, etc.)',
      action: 'See records below'
    },
    {
      step: 4,
      title: 'Verify Domain',
      description: 'Wait for DNS propagation (5-30 minutes) and check verification status',
      note: 'AWS will automatically detect the records'
    },
    {
      step: 5,
      title: 'Request Production Access',
      description: 'Move out of sandbox mode to send to any email address',
      link: 'https://docs.aws.amazon.com/ses/latest/dg/request-production-access.html'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold mb-4">
            📧 Happy Human Tools - AWS SES Setup
          </h1>
          <p className="text-xl text-gray-400">
            Complete email infrastructure setup for <span className="text-blue-400 font-mono">{domain}</span>
          </p>
        </motion.div>

        {/* AWS Setup Steps */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-gray-800 rounded-lg p-6 mb-8"
        >
          <h2 className="text-2xl font-bold mb-6">🚀 AWS SES Configuration Steps</h2>
          <div className="space-y-4">
            {awsSESSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * index }}
                className="flex gap-4 items-start"
              >
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center font-bold shrink-0">
                  {step.step}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg">{step.title}</h3>
                  <p className="text-gray-400">{step.description}</p>
                  {step.link && (
                    <a
                      href={step.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:underline text-sm mt-1 inline-block"
                    >
                      Open in AWS Console →
                    </a>
                  )}
                  {step.input && (
                    <div className="mt-2 p-2 bg-black/30 rounded font-mono text-sm">
                      {step.input}
                    </div>
                  )}
                  {step.note && (
                    <p className="text-yellow-400 text-sm mt-2">⚠️ {step.note}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* DNS Records */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-gray-800 rounded-lg p-6"
        >
          <h2 className="text-2xl font-bold mb-6">🔧 DNS Records to Add</h2>
          <p className="text-gray-400 mb-6">
            Add these records to your DNS provider (CloudFlare, Route53, etc.)
          </p>

          <div className="space-y-4">
            {dnsRecords.map((record, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * index }}
                className="bg-black/30 rounded-lg p-4 border border-gray-700"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className="inline-block px-2 py-1 bg-blue-600 rounded text-xs font-bold mr-2">
                      {record.type}
                    </span>
                    <span className="text-gray-400 text-sm">{record.purpose}</span>
                  </div>
                  <button
                    onClick={() => copyToClipboard(record.value, record.name)}
                    className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-sm transition-colors"
                  >
                    {copiedRecord === record.name ? '✓ Copied' : 'Copy Value'}
                  </button>
                </div>

                <div className="space-y-2 font-mono text-sm">
                  <div>
                    <span className="text-gray-500">Name:</span>
                    <div className="text-white break-all bg-black/50 p-2 rounded mt-1">
                      {record.name}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Value:</span>
                    <div className="text-green-400 break-all bg-black/50 p-2 rounded mt-1">
                      {record.value}
                    </div>
                  </div>
                  {record.priority && (
                    <div>
                      <span className="text-gray-500">Priority:</span>
                      <span className="text-white ml-2">{record.priority}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Quick Commands */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-8 bg-gradient-to-br from-blue-900/30 to-purple-900/30 rounded-lg p-6 border border-blue-600/30"
        >
          <h3 className="text-xl font-bold mb-4">⚡ Quick AWS CLI Commands</h3>
          <div className="space-y-3 font-mono text-sm">
            <div className="p-3 bg-black/50 rounded">
              <p className="text-gray-400 text-xs mb-1"># Verify domain identity</p>
              <p className="text-green-400">aws ses verify-domain-identity --domain {domain} --region us-west-2</p>
            </div>
            <div className="p-3 bg-black/50 rounded">
              <p className="text-gray-400 text-xs mb-1"># Check verification status</p>
              <p className="text-green-400">aws ses get-identity-verification-attributes --identities {domain} --region us-west-2</p>
            </div>
            <div className="p-3 bg-black/50 rounded">
              <p className="text-gray-400 text-xs mb-1"># Enable DKIM signing</p>
              <p className="text-green-400">aws ses put-identity-dkim-attributes --identity {domain} --dkim-enabled --region us-west-2</p>
            </div>
            <div className="p-3 bg-black/50 rounded">
              <p className="text-gray-400 text-xs mb-1"># Test email sending</p>
              <p className="text-green-400">aws ses send-email --from hello@{domain} --to test@example.com --subject "Test" --text "Hello from Happy Human Tools!" --region us-west-2</p>
            </div>
          </div>
        </motion.div>

        {/* Status Checklist */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-8 bg-gray-800 rounded-lg p-6"
        >
          <h3 className="text-xl font-bold mb-4">✅ Verification Checklist</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" className="w-5 h-5" />
              <span>Domain verification TXT record added</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" className="w-5 h-5" />
              <span>DKIM CNAME records added (all 3)</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" className="w-5 h-5" />
              <span>SPF record added</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" className="w-5 h-5" />
              <span>DMARC record configured</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" className="w-5 h-5" />
              <span>MX records set up</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" className="w-5 h-5" />
              <span>Domain verified in AWS SES</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" className="w-5 h-5" />
              <span>DKIM verification complete</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" className="w-5 h-5" />
              <span>Production access requested</span>
            </label>
          </div>
        </motion.div>

        {/* Next Steps */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-8 text-center"
        >
          <p className="text-gray-400 mb-4">
            Once all records are added and verified, you'll be able to send emails from:
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <span className="px-4 py-2 bg-blue-600/20 border border-blue-600 rounded font-mono">
              hello@happyhumantools.com
            </span>
            <span className="px-4 py-2 bg-blue-600/20 border border-blue-600 rounded font-mono">
              support@happyhumantools.com
            </span>
            <span className="px-4 py-2 bg-blue-600/20 border border-blue-600 rounded font-mono">
              noreply@happyhumantools.com
            </span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};